import NotificationsOutlined from '@icons/NotificationsOutlined';
import { Badge, Box, Button, CircularProgress, Divider, IconButton, Popover, Switch, Typography, useTheme } from '@mui/material';
import { useDeleteAllNotificationsMutation, useGetNotificationsQuery, useSeenAllNotificationsMutation, useSeenNotificationMutation } from '@service';
import { NotificationType } from '@types';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import yn from 'yn';

import NavbarMailboxCampaignAccessNotification from './NavbarMailboxCampaignAccessNotification';
import NavbarMailboxCampaignNotification from './NavbarMailboxCampaignNotification';
import NavbarMailboxCampaignPermissionsNotification from './NavbarMailboxCampaignPermissionsNotification';
import NavbarMailboxTackleboxNotification from './NavbarMailboxTackleboxNotification';
import NavbarMailboxTackleboxShareNotification from './NavbarMailboxTackleboxShareNotification';
import NavbarMailboxViewAccessNotification from './NavbarMailboxViewAccessNotification';

const { VITE_DISABLE_NOTIFICATION_REFETCH } = import.meta.env;
const disableRefetch = yn(VITE_DISABLE_NOTIFICATION_REFETCH);

const NavbarMailbox = () => {
  const theme = useTheme();

  const [unreadOnly, setUnreadOnly] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLButtonElement>(null);

  const [updateSeen] = useSeenNotificationMutation();
  const [seenAllNotifications] = useSeenAllNotificationsMutation();
  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();

  const onMailboxOpened = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const onMailboxClosed = useCallback(() => {
    setMenuAnchorEl(null);
    seenAllNotifications();
  }, [seenAllNotifications]);

  const onNavigate = useCallback(
    (id?: string) => {
      setMenuAnchorEl(null);
      if (id) {
        updateSeen(id);
      }
    },
    [updateSeen]
  );

  const onToggleUnreadOnly = useCallback(() => {
    setUnreadOnly((prev) => !prev);
  }, []);

  const onDeleteAllNotifications = useCallback(() => {
    deleteAllNotifications();
  }, [deleteAllNotifications]);

  const { data, isLoading, isError, refetch } = useGetNotificationsQuery();

  // automatically refetch notifications every 60 seconds (unless disabled by environment variable):
  useEffect(() => {
    if (disableRefetch) {
      return; // Skip setting up the interval if refetch is disabled
    }

    const interval = setInterval(() => {
      refetch();
    }, 60000);
    return () => clearInterval(interval);
  }, [refetch]);

  const numSeen = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.filter((d) => !d.seen).length;
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data || data.length == 0) return [];
    if (unreadOnly) {
      return data.filter((notification) => !notification.seen);
    }
    return data;
  }, [data, unreadOnly]);

  return (
    <>
      <Popover
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={onMailboxClosed}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
            width: 379,
            maxHeight: 500
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              p: (theme) => theme.spacing(1, 2)
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '24px',
                letterSpacing: '0px',
                flex: 1
              }}
            >
              Notifications
            </Typography>
            <Switch value={unreadOnly} onChange={onToggleUnreadOnly} />
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '143%',
                letterSpacing: '0px'
              }}
            >
              Show unread
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={onDeleteAllNotifications}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                minWidth: 0,
                padding: theme.spacing(0.5)
              }}
            >
              Clear All
            </Button>
          </Box>
          <Divider />
          {/* The header stays put while the notifications scroll */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {isError && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: (theme) => theme.spacing(2, 0)
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0px'
                  }}
                >
                  Failed to load notifications.
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '143%',
                    letterSpacing: '0px',
                    color: (theme) => theme.palette.text.secondary
                  }}
                >
                  Please try again later.
                </Typography>
              </Box>
            )}
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 102, // match notification message height
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {filteredData && filteredData.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: (theme) => theme.spacing(2, 0)
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '150%',
                    letterSpacing: '0px'
                  }}
                >
                  You currently have no notifications.
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '143%',
                    letterSpacing: '0px',
                    color: (theme) => theme.palette.text.secondary
                  }}
                >
                  {unreadOnly ? 'Unread notifications will appear here.' : 'All notifications will appear here.'}
                </Typography>
              </Box>
            )}
            {filteredData &&
              filteredData.length > 0 &&
              filteredData.map((notification) => {
                if (notification.type === NotificationType.CAMPAIGN) {
                  return <NavbarMailboxCampaignNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else if (notification.type === NotificationType.TACKLEBOX) {
                  return <NavbarMailboxTackleboxNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else if (notification.type === NotificationType.CAMPAIGN_INVITE) {
                  return <NavbarMailboxCampaignPermissionsNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else if (notification.type === NotificationType.CAMPAIGN_ACCESS) {
                  return <NavbarMailboxCampaignAccessNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else if (notification.type === NotificationType.TACKLEBOX_SHARE) {
                  return <NavbarMailboxTackleboxShareNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else if (notification.type === NotificationType.VIEW) {
                  return <NavbarMailboxViewAccessNotification key={notification.id} notification={notification} onNavigate={onNavigate} />;
                } else {
                  console.warn('Unknown notification type', notification.type);
                }
              })}
          </Box>
        </Box>
      </Popover>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'stretch',
          height: '48px'
        }}
      >
        <Badge badgeContent={numSeen} color="primary">
          <IconButton size="small" onClick={onMailboxOpened}>
            <NotificationsOutlined fontSize="small" />
          </IconButton>
        </Badge>
      </Box>
    </>
  );
};
export default NavbarMailbox;
